import { graphql } from 'react-apollo';

import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import mapExists from 'bundles/enterprise-legacy-learner-home/utils/mapExists';
import { LevelSetsAttemptsQuery } from 'bundles/program-skills-objectives/components/ProgramSkillsObjectivesPage/LevelSetGraphqlQueries';
import type {
  LevelSetAttempt,
  LevelSetsAttemptsResults,
  LevelSetsAttemptsVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillsObjectivesPage/LevelSetGraphqlQueries';
import {
  convertLearnerDiagnosticIdToBaseSkillId,
  convertTSPSkillIdToBaseSkillId,
} from 'bundles/program-skills-objectives/utils';

type PropsFromCaller = {
  userId: number | undefined;
  skillIds: string[];
};

export type PropsFromLevelSetAttemptGraphql = {
  levelSetAttempts?: LevelSetAttempt[];
  levelSetAttemptLoading: boolean;
  levelSetAttemptError: boolean;
};

/**
 * This function returns a HOC which fetches the level set attempts for the given skill ids and userid.
 * The user id and skill ids need to be passed in to the HOC that is returned.
 */
const withLevelSetsAttempts = () =>
  graphql<PropsFromCaller, LevelSetsAttemptsResults, LevelSetsAttemptsVariables, PropsFromLevelSetAttemptGraphql>(
    LevelSetsAttemptsQuery,
    {
      options: ({ userId, skillIds }) => ({
        variables: {
          // See skip below
          userId: userId!.toString(),
          skillIds: skillIds.map((skillId) => convertTSPSkillIdToBaseSkillId(skillId)).join(','),
        },
      }),
      skip: ({ userId }) => !userId,
      props: ({ data }) => {
        const levelSetAttemptLoading = data?.loading ?? true;
        const levelSetAttemptError = !!data?.error;

        const levelSetAttempts = mapExists(
          filterExistsOrDefault(data?.LevelSetsAttemptsQuery?.elements),
          (levelSetAttempt) => {
            const skillId = convertLearnerDiagnosticIdToBaseSkillId(levelSetAttempt.id);
            return { ...levelSetAttempt, skillId };
          }
        );
        return { levelSetAttempts, levelSetAttemptLoading, levelSetAttemptError };
      },
    }
  );

export default withLevelSetsAttempts;
